// src/input/remote.js
// Filepath: // src/input/remote.js
// Robust DPAD navigation with strict scopes, numeric guards, and per-row column memory.
// Now also routes Android 'hardwareBack' (from MainActivity) through the same onBack pipeline.
// Updates:
//  - focusRowCol(row,col,{retryMs}) will retry until the element exists.
//  - getFocused() upgrades the "fallback" to a true focused element if nothing is focused yet.
//  - installRemote() seeds initial focus after a frame.
//  - Scene handlers may override low-level key handling via onKeyDown/onKeyUp.

const Keys = {
  LEFT: ["ArrowLeft", "Left"],
  RIGHT: ["ArrowRight", "Right"],
  UP: ["ArrowUp", "Up"],
  DOWN: ["ArrowDown", "Down"],
  ENTER: ["Enter", "OK", " "],
  BACK: ["Backspace", "Escape", "BrowserBack", "GoBack"],
};

let sceneHandlers = {
  onEnter: null,
  onBack: null,
  onLeft: null,
  onRight: null,
  onUp: null,
  onDown: null,
  onKeyDown: null, // low-level override, receive KeyboardEvent, return true if handled
  onKeyUp: null,   // low-level override for keyup
};

let lastRailFocus = null;       // element
const lastColByRow = new Map(); // row -> col
let lastRowIndex = null;

export function setSceneHandlers(handlers = {}) {
  sceneHandlers = {
    onEnter: handlers.onEnter || null,
    onBack: handlers.onBack || null,
    onLeft: handlers.onLeft || null,
    onRight: handlers.onRight || null,
    onUp: handlers.onUp || null,
    onDown: handlers.onDown || null,
    onKeyDown: handlers.onKeyDown || null,
    onKeyUp: handlers.onKeyUp || null,
  };
}

// --- Row memory API
export function getRememberedCol(row) {
  const v = lastColByRow.get(row);
  return Number.isFinite(v) ? v : null;
}
export function getLastRowIndex() {
  return Number.isFinite(lastRowIndex) ? lastRowIndex : null;
}
export function clearRowMemory() {
  lastColByRow.clear();
  lastRowIndex = null;
}

/**
 * Focus the element at (row, col). If it is not yet in the DOM, retry until it is.
 */
export function focusRowCol(row, col, opts = {}) {
  if (!Number.isFinite(row) || !Number.isFinite(col)) return false;

  const retryMs = Math.max(0, Number(opts.retryMs ?? 0));
  const start =
    typeof performance !== "undefined" ? performance.now() : Date.now();

  function tryOnce() {
    const scope = railsScope();
    const candidates = sameRowCandidates(scope, row);
    const el =
      candidates.find((n) => parseIndex(n, "col") === col) ||
      candidates[0] ||
      null;

    if (el) {
      focusEl(el);
      return true;
    }

    if (retryMs > 0) {
      const now =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      if (now - start < retryMs) {
        if (typeof requestAnimationFrame === "function") {
          requestAnimationFrame(tryOnce);
        } else {
          setTimeout(tryOnce, 16);
        }
        return true;
      }
    }
    return false;
  }

  return tryOnce();
}

/** Snapshots for sandboxed memory per scene */
export function snapshotRowMemory() {
  return {
    lastRowIndex,
    lastColByRow: Array.from(lastColByRow.entries()),
  };
}
export function restoreRowMemory(snap) {
  try {
    lastColByRow.clear();
    if (snap?.lastColByRow) {
      for (const [k, v] of snap.lastColByRow) {
        if (Number.isFinite(k) && Number.isFinite(v)) {
          lastColByRow.set(k, v);
        }
      }
    }
    lastRowIndex = Number.isFinite(snap?.lastRowIndex)
      ? snap.lastRowIndex
      : null;
  } catch {
    // no-op safeguard
  }
}

function match(code, list) {
  return list.includes(code);
}
function parseIndex(el, attr) {
  const v = Number(el?.dataset?.[attr]);
  return Number.isFinite(v) ? v : null;
}
function inSideNav(el) {
  return !!el?.closest?.('[data-scope="sidenav"]');
}
function railsScope() {
  return (
    document.querySelector('[data-scope="rails"]') ||
    document.querySelector('[data-rails-root]') ||
    document
  );
}
function isInDocument(el) {
  return !!(el && el.ownerDocument && el.ownerDocument.contains(el));
}

/**
 * Returns the currently focused tile element if any.
 */
function getFocused() {
  const tagged = document.querySelector(
    '[data-focusable="true"].focused'
  );
  if (tagged) return tagged;

  const ae = document.activeElement;
  if (ae) {
    const nearest = ae.closest?.('[data-focusable="true"]');
    if (nearest) return ensureFocused(nearest);
  }

  const railsFirst = railsScope().querySelector(
    '[data-focusable="true"]'
  );
  if (railsFirst) return ensureFocused(railsFirst);

  const any = document.querySelector('[data-focusable="true"]');
  if (any) return ensureFocused(any);

  return null;
}

function ensureFocused(el) {
  if (!el.classList?.contains("focused")) {
    focusEl(el);
  }
  return el;
}

function focusEl(el) {
  if (!el) return;
  document
    .querySelectorAll(".focused")
    .forEach((n) => n.classList.remove("focused"));

  if (!el.hasAttribute("tabindex")) {
    el.setAttribute("tabindex", "-1");
  }

  el.classList.add("focused");
  el.focus?.({ preventScroll: true });

  if (!inSideNav(el)) {
    const r = parseIndex(el, "row");
    const c = parseIndex(el, "col");
    if (r != null && c != null) {
      lastColByRow.set(r, c);
      lastRowIndex = r;
    }
    lastRailFocus = el;
  }
}

function sameRowCandidates(scopeEl, row) {
  const all = scopeEl.querySelectorAll(
    `[data-focusable="true"][data-row="${row}"]`
  );
  const arr = [];
  all.forEach((n) => {
    const c = parseIndex(n, "col");
    if (c != null) arr.push(n);
  });
  arr.sort(
    (a, b) => parseIndex(a, "col") - parseIndex(b, "col")
  );
  return arr;
}

function moveHorizontal(dir) {
  const cur = getFocused();
  if (!cur) return;

  const scope = inSideNav(cur)
    ? cur.closest('[data-scope="sidenav"]')
    : railsScope();
  const row = parseIndex(cur, "row") ?? 0;

  // From side nav to rails
  if (inSideNav(cur) && dir > 0) {
    window.dispatchEvent(new CustomEvent("sidenav-collapse"));
    if (isInDocument(lastRailFocus)) {
      return focusEl(lastRailFocus);
    }

    const rememberedCol = lastColByRow.get(0) ?? 0;
    const candidates0 = sameRowCandidates(railsScope(), 0);
    const candidate =
      candidates0.find(
        (n) => parseIndex(n, "col") === rememberedCol
      ) || candidates0[0];
    if (candidate) return focusEl(candidate);
    return;
  }

  const sameRow = sameRowCandidates(scope, row);
  const idx = sameRow.indexOf(cur);
  const next = sameRow[idx + dir];

  if (next) return focusEl(next);

  // At left edge of rails move into side nav
  if (!inSideNav(cur) && dir < 0 && idx === 0) {
    const sideFirst = document.querySelector(
      '[data-scope="sidenav"] [data-focusable="true"][data-row="0"][data-col="0"]'
    );
    if (sideFirst) return focusEl(sideFirst);
  }
}

function moveVertical(dir) {
  const cur = getFocused();
  if (!cur) return;

  if (inSideNav(cur)) {
    const scope = cur.closest('[data-scope="sidenav"]');
    const row = parseIndex(cur, "row") ?? 0;

    const rowsSet = new Set(
      Array.from(
        scope.querySelectorAll(
          '[data-focusable="true"][data-row]'
        )
      )
        .map((n) => parseIndex(n, "row"))
        .filter((n) => n != null)
    );
    const rows = Array.from(rowsSet).sort((a, b) => a - b);
    if (!rows.length) return;

    let targetRow = null;
    if (dir > 0) {
      targetRow = rows.find((r) => r > row) ?? null;
    } else {
      for (let i = rows.length - 1; i >= 0; i -= 1) {
        if (rows[i] < row) {
          targetRow = rows[i];
          break;
        }
      }
    }

    if (targetRow == null) return;

    const candidate = scope.querySelector(
      `[data-focusable="true"][data-row="${targetRow}"][data-col="0"]`
    );
    if (candidate) focusEl(candidate);
    return;
  }

  const scope = railsScope();
  const row = parseIndex(cur, "row") ?? 0;

  const rowsSet = new Set(
    Array.from(
      scope.querySelectorAll(
        '[data-focusable="true"][data-row]'
      )
    )
      .map((n) => parseIndex(n, "row"))
      .filter((n) => n != null && n >= 0)
  );
  const rows = Array.from(rowsSet).sort((a, b) => a - b);
  if (!rows.length) return;

  let targetRow = null;
  if (dir > 0) {
    targetRow = rows.find((r) => r > row) ?? null;
  } else {
    for (let i = rows.length - 1; i >= 0; i -= 1) {
      if (rows[i] < row) {
        targetRow = rows[i];
        break;
      }
    }
  }

  if (targetRow == null) return;

  const rememberedCol = lastColByRow.get(targetRow);
  const wantCol = Number.isFinite(rememberedCol)
    ? rememberedCol
    : 0;

  const candidates = sameRowCandidates(scope, targetRow);
  if (!candidates.length) return;

  let next = candidates.find(
    (n) => parseIndex(n, "col") === wantCol
  );
  if (!next) {
    next = candidates.reduce((best, n) => {
      const d = Math.abs(
        (parseIndex(n, "col") ?? 0) - wantCol
      );
      const bd = best
        ? Math.abs(
            (parseIndex(best, "col") ?? 0) - wantCol
          )
        : Infinity;
      return d < bd ? n : best;
    }, null);
  }
  if (next) focusEl(next);
}

function clickFocused() {
  const el = getFocused();
  if (!el) return;

  const isClickableSelf =
    el.tagName === "BUTTON" ||
    el.tagName === "A" ||
    el.classList?.contains?.("MuiButtonBase-root") ||
    (el.getAttribute?.("role") || "").includes(
      "button"
    );

  if (isClickableSelf && typeof el.click === "function") {
    el.click();
    return;
  }

  const selector =
    el.dataset.clickTarget ||
    "button, .MuiButtonBase-root, [role=\"button\"], [data-click=\"true\"]";
  const target = el.querySelector(selector);

  if (target && typeof target.click === "function") {
    target.click();
  }
}

function handleKeyDown(e) {
  // Scene-level low-level override (e.g. player seek ramp)
  if (typeof sceneHandlers.onKeyDown === "function") {
    const handled = sceneHandlers.onKeyDown(e) === true;
    if (handled) {
      e.preventDefault();
      return;
    }
  }

  const k = e.key;

  if (match(k, Keys.LEFT)) {
    e.preventDefault();
    if (sceneHandlers.onLeft?.() === true) return;
    moveHorizontal(-1);
    return;
  }
  if (match(k, Keys.RIGHT)) {
    e.preventDefault();
    if (sceneHandlers.onRight?.() === true) return;
    moveHorizontal(1);
    return;
  }
  if (match(k, Keys.UP)) {
    e.preventDefault();
    if (sceneHandlers.onUp?.() === true) return;
    moveVertical(-1);
    return;
  }
  if (match(k, Keys.DOWN)) {
    e.preventDefault();
    if (sceneHandlers.onDown?.() === true) return;
    moveVertical(1);
    return;
  }
  if (match(k, Keys.ENTER)) {
    e.preventDefault();
    if (sceneHandlers.onEnter?.() === true) return;
    clickFocused();
    return;
  }
  if (match(k, Keys.BACK)) {
    e.preventDefault();
    if (sceneHandlers.onBack?.() === true) return;
    return;
  }
}

function handleKeyUp(e) {
  // Let scenes stop hold/ramp behavior or similar.
  if (typeof sceneHandlers.onKeyUp === "function") {
    const handled = sceneHandlers.onKeyUp(e) === true;
    if (handled) {
      e.preventDefault();
    }
  }
}

export function installRemote() {
  if (typeof window === "undefined") {
    return () => {};
  }

  window.addEventListener("keydown", handleKeyDown, {
    passive: false,
  });
  window.addEventListener("keyup", handleKeyUp, {
    passive: false,
  });

  // Map Android hardware back to Backspace semantics.
  const onHardwareBack = () => {
    const synthetic = new KeyboardEvent("keydown", {
      key: "Backspace",
      bubbles: true,
      cancelable: true,
    });
    handleKeyDown(synthetic);
  };
  window.addEventListener(
    "hardwareBack",
    onHardwareBack,
    { passive: false }
  );

  // Seed initial focus.
  const seed = () => {
    const scope = railsScope();
    const firstRails =
      scope.querySelector(
        '[data-focusable="true"][data-row="0"][data-col="0"]'
      ) ||
      scope.querySelector('[data-focusable="true"]');

    if (firstRails) {
      focusEl(firstRails);
    }
  };
  if (typeof requestAnimationFrame === "function") {
    requestAnimationFrame(seed);
  } else {
    setTimeout(seed, 16);
  }

  return () => {
    window.removeEventListener("keydown", handleKeyDown);
    window.removeEventListener("keyup", handleKeyUp);
    window.removeEventListener("hardwareBack", onHardwareBack);
  };
}
